/**
 * OKR Store - OKR 센터패널 및 피드백 관리 (리팩토링 버전)
 *
 * @description OKR 목록 조회, 피드백 CRUD, 분기 관리
 *
 * @usage
 * const { fetchOkrListForMember, fetchCycleList, saveOkrFeedback } = useOkrStore();
 */
import { ref, reactive, computed, nextTick } from 'vue';
import { useKpiApi } from '~/composables/performance/kpi/useKpiApi';
import { useUiToast } from '~/composables/useUiToast';
import { kpiT } from '~/composables/performance/kpi/useKpiI18n';

// 공유 상태 import
import {
  selectedYear,
  selectedMember,
  loginUserInfo,
  selectedKpi,
  resetDetailData,
  kpiList, // ← 추가
  goalTypeFilter, // ← 추가
  selectedMemberDomains, // ← 열람 권한 도메인 (KPI↔OKR 누수 차단)
} from './useKpiSharedState';

// 공통 유틸리티 import
import { mapOkrStatus, mapTaskStatus, getStatusColor, resolveDefaultCycleId } from './useKpiUtils';

// 캐시/동기화 헬퍼 import
import { useOkrSync } from './useOkrSync';

import type {
  OkrRecord,
  OkrGrouped,
  TaskRecord,
  CycleItem,
  OkrFeedbackItem,
  FeedbackRatingOption,
  OkrObjResponseItem,
  OkrKrResponseItem,
  PlanResponseItem,
  FeedbackResponseItem,
  FeedbackStatusResponseItem,
  KpiOkrMapObjective,
} from './useKpiTypes';

// ============================================
// 싱글톤 상태 변수
// ============================================

// 분기(Cycle) 관련
const cycleList = ref<CycleItem[]>([]);
const selectedCycleId = ref<string>('');

// OKR 센터패널 목록
const okrListForMember = ref<OkrGrouped[]>([]);
const isOkrLoading = ref(false);
const selectedOkr = ref<OkrRecord | null>(null);

// KR 연결 업무
const krTaskRecords = ref<TaskRecord[]>([]);
const isKrTaskLoading = ref(false);

// 패널 아코디언 상태
const isKpiPanelExpanded = ref(true);
const isOkrPanelExpanded = ref(true);

// OKR 피드백
const okrFeedbackList = ref<OkrFeedbackItem[]>([]);
const okrStatusOptions = ref<{ value: string; label: string; color: string }[]>([]);
const feedbackRatingList = ref<FeedbackRatingOption[]>([]);
const isOkrFeedbackLoading = ref(false);
const activeOkrDetailTab = ref('실적');

// OKR 피드백 입력 폼
const okrFeedbackForm = ref({
  content: '',
  achive: '',
  actual: '',
  statusCd: '',
  feedbackRatingId: '3',
  feedbackId: '',
});

// 피드백 아이콘 설정 (feedbackEvalMtd: 01=점수, 02=별, 03=하트, 04=따봉, 05=등급)
const feedbackEvalMtd = ref<string>('02');

// 목표-업무 연계도 아코디언 상태 (PerformanceGoalTable용) — 여러 KPI 동시 펼침 지원
const expandedKpiIds = reactive(new Set<string>());
const isAllExpanded = ref(false);
const kpiOkrMap = reactive<Record<string, KpiOkrMapObjective[]>>({});
const okrLoadingCount = ref(0);
const okrLoading = computed(() => okrLoadingCount.value > 0);
// KR 펼침 상태 — 여러 KR 동시 펼침 지원 (KPI/OBJ와 동일하게 Set 사용)
const expandedKrIds = reactive(new Set<string>());
const krPlanMap = reactive<Record<string, PlanResponseItem[]>>({});
const planLoading = ref(false);
const kpiTaskMap = reactive<Record<string, PlanResponseItem[]>>({});
const taskLoadingCount = ref(0);
const taskLoading = computed(() => taskLoadingCount.value > 0);

// 미연계 OKR (KPI에 연결되지 않은 OKR)
const unlinkedOkrList = ref<KpiOkrMapObjective[]>([]);
const unlinkedOkrLoading = ref(false);
const isUnlinkedSectionExpanded = ref(false);

// KR 상세 보강 캐시 (performMeasMtd/performTarget/performUnit만 캐시, actual은 매번 조회)
const krEnrichCache = new Map<string, { performMeasMtd: string; performTarget: string; performUnit: string; weight: number }>();

// ============================================
// State Export
// ============================================
export const useOkrState = () => {
  return {
    // 분기
    cycleList,
    selectedCycleId,

    // OKR 목록
    okrListForMember,
    isOkrLoading,
    selectedOkr,

    // KR 업무
    krTaskRecords,
    isKrTaskLoading,

    // 패널 상태
    isKpiPanelExpanded,
    isOkrPanelExpanded,

    // 피드백
    okrFeedbackList,
    okrStatusOptions,
    feedbackRatingList,
    isOkrFeedbackLoading,
    activeOkrDetailTab,
    okrFeedbackForm,
    feedbackEvalMtd,

    // 아코디언 상태
    expandedKpiIds,
    isAllExpanded,
    kpiOkrMap,
    okrLoading,
    expandedKrIds,
    krPlanMap,
    planLoading,
    kpiTaskMap,
    taskLoading,

    // 미연계 OKR
    unlinkedOkrList,
    unlinkedOkrLoading,
    isUnlinkedSectionExpanded,
  };
};

// ============================================
// Method Export
// ============================================
export const useOkrStore = () => {
  const kpiApi = useKpiApi();

  // 캐시/동기화 헬퍼 초기화
  const {
    getOkrCacheKey,
    getKrPlanCacheKey,
    clearGoalOkrCache,
    syncObjNameInAllCaches,
    syncKrNameInAllCaches,
    syncKrStatusInAllCaches,
    syncKrNewYnInAllCaches,
  } = useOkrSync({
    kpiOkrMap,
    krPlanMap,
    kpiTaskMap,
    okrListForMember,
    unlinkedOkrList,
    expandedKpiIds,
    isAllExpanded,
    expandedKrIds,
    selectedCycleId,
  });

  /**
   * [열람 권한 차단용] 선택 대상자의 OKR 화면 상태 일괄 초기화
   *
   * ⚠️ KPI/OKR 통합 화면 누수 차단 핵심
   * - selectedMemberDomains.okr === false (= OKR 열람 권한 없는 대상자)일 때
   *   OKR을 "조회하지 않고" 기존 상태/캐시를 비워 정보 노출을 막는다.
   * - clearGoalOkrCache: kpiOkrMap / krPlanMap / kpiTaskMap / unlinkedOkrList 비움
   * - 추가로 센터패널 목록(okrListForMember)과 분기 상태도 비운다.
   */
  const resetOkrForMember = () => {
    clearGoalOkrCache();
    okrListForMember.value = [];
    cycleList.value = [];
    selectedCycleId.value = '';
  };

  // ============================================
  // 분기 관리
  // ============================================

  /**
   * 분기 목록 조회
   */
  const fetchCycleList = async () => {
    try {
      const response = await kpiApi.fetchCycleList(selectedYear.value);

      if (response?.cycleList && Array.isArray(response.cycleList)) {
        cycleList.value = response.cycleList;

        if (selectedCycleId.value) {
          const existsInList = cycleList.value.some(c => c.cycleId === selectedCycleId.value);
          if (!existsInList) {
            selectedCycleId.value = resolveDefaultCycleId(cycleList.value);
          }
        } else {
          // 현재 월 기준 기본 분기 설정 (해당 분기 없으면 첫 번째 분기)
          selectedCycleId.value = resolveDefaultCycleId(cycleList.value);
        }
      } else {
        cycleList.value = [];
        selectedCycleId.value = '';
      }
    } catch (error) {
      console.error('분기 목록 조회 실패:', error);
      cycleList.value = [];
      selectedCycleId.value = '';
    }
  };

  /**
   * 분기 변경 핸들러
   */
  const changeCycle = async (cycleId: string) => {
    selectedCycleId.value = cycleId;
    await fetchOkrListForMember();
  };

  // ============================================
  // OKR 목록 조회
  // ============================================

  /**
   * 구성원의 OKR 목록 조회 (센터패널용)
   */
  const fetchOkrListForMember = async (userId?: string) => {
    const targetUserId = userId || selectedMember.value || loginUserInfo.value?.userId;

    if (!targetUserId) {
      return;
    }

    // [열람 권한 차단] 선택 대상자의 OKR 열람 권한이 없으면 조회하지 않고 비움
    // (KPI 공유자로만 등록된 대상자 → 센터패널 OKR 노출/조회 모두 차단)
    if (!selectedMemberDomains.value.okr) {
      okrListForMember.value = [];
      return;
    }

    isOkrLoading.value = true;

    try {
      let response;

      if (selectedCycleId.value) {
        response = await kpiApi.fetchOkrListByCycle(
          selectedYear.value,
          selectedCycleId.value,
          targetUserId,
        );

        const objList = response?.objList || [];
        const krList = response?.krList || [];

        if (Array.isArray(objList) && objList.length > 0) {
          // KR을 OBJ_ID로 그룹핑
          const krByObjId = new Map<string, OkrKrResponseItem[]>();
          krList.forEach((kr: OkrKrResponseItem) => {
            const objId = kr.objId || '';
            if (!krByObjId.has(objId)) {
              krByObjId.set(objId, []);
            }
            krByObjId.get(objId)!.push(kr);
          });

          // Objective + KR 계층 구조 생성
          okrListForMember.value = objList.map((obj: OkrObjResponseItem) => {
            const objId = obj.objId || '';
            const krs = krByObjId.get(objId) || [];

            // OBJ 달성률: KR 가중치 기반 프론트 계산 (백엔드 obj.progress 대신)
            let objProgress = 0;
            if (krs.length > 0) {
              const krItems = krs.map((kr: OkrKrResponseItem) => ({
                progress: Number(kr.achive) || Number(kr.progress) || 0,
                weight: Number(kr.weight) || 0,
              }));
              const totalWeight = krItems.reduce((sum, k) => sum + k.weight, 0);
              if (totalWeight > 0) {
                const weightedSum = krItems.reduce((sum, k) => sum + k.weight * k.progress, 0);
                objProgress = Math.round((weightedSum / totalWeight) * 10) / 10;
              } else {
                const totalProgress = krItems.reduce((sum, k) => sum + k.progress, 0);
                objProgress = Math.round((totalProgress / krItems.length) * 10) / 10;
              }
            }

            return {
              objId,
              objNm: obj.objNm || '',
              progress: objProgress,
              color: obj.color || '',
              isExpanded: true,
              keyResults: krs.map((kr: OkrKrResponseItem) => ({
                krId: kr.krId || '',
                krNm: kr.krNm || '',
                krDesc: kr.content || '',
                targetVal: kr.performTarget || kr.target || '-',
                currentVal: kr.actual || '-',
                progress: Number(kr.achive) || Number(kr.progress) || 0,
                weight: Number(kr.weight) || 0,
                status: mapOkrStatus(kr.statusCd || ''),
                statusColor: kr.statusColor || getStatusColor(kr.statusCd || ''),
                // 상태 설정(hcm_config_status)에서 온 이름/색. AS-IS 그리드가 그대로 쓰는 값이라
                // 매니저 대시보드 KR 그리드가 필요로 한다. status 는 3분류라 이름을 대체하지 못한다.
                statusNm: kr.statusNm || '',
                color: kr.color || '',
                // 상태명이 비어 오는 경우가 있어(selectObjKrData_json) 코드로 되찾을 수 있게 남긴다
                feedbackStatusCd: kr.feedbackStatusCd || '',
              })),
            };
          });
        } else if (Array.isArray(krList) && krList.length > 0) {
          // objList가 없지만 krList가 있는 경우
          const filteredKrList = krList.filter((kr: OkrKrResponseItem) => kr.objNm);
          const groupedMap = new Map<string, OkrGrouped>();

          filteredKrList.forEach((kr: OkrKrResponseItem) => {
            const objNm = kr.objNm || '';
            const objId = kr.objId || objNm;

            if (!groupedMap.has(objId)) {
              groupedMap.set(objId, {
                objId,
                objNm,
                progress: 0,
                isExpanded: true,
                keyResults: [],
              });
            }

            const group = groupedMap.get(objId)!;
            group.keyResults.push({
              krId: kr.krId || '',
              krNm: kr.krNm || '',
              krDesc: kr.content || '',
              targetVal: kr.performTarget || kr.target || '-',
              currentVal: kr.actual || '-',
              progress: Number(kr.achive) || Number(kr.progress) || 0,
              weight: Number(kr.weight) || 0,
              status: mapOkrStatus(kr.statusCd || ''),
              statusColor: kr.statusColor || getStatusColor(kr.statusCd || ''),
              // 위 분기와 동일 — 상태 설정에서 온 이름/색을 버리지 않는다
              statusNm: kr.statusNm || '',
              color: kr.color || '',
              feedbackStatusCd: kr.feedbackStatusCd || '',
            });
          });

          // 각 Objective의 진행률 계산 (가중치 반영)
          groupedMap.forEach(group => {
            if (group.keyResults.length > 0) {
              const totalWeight = group.keyResults.reduce((sum, kr) => sum + kr.weight, 0);
              if (totalWeight > 0) {
                // 가중 평균: (가중치 × 진행률)의 합 / 가중치 합
                const weightedProgress = group.keyResults.reduce((sum, kr) => sum + kr.weight * kr.progress, 0);
                group.progress = Math.round((weightedProgress / totalWeight) * 10) / 10;
              } else {
                // 가중치가 없으면 단순 평균
                const totalProgress = group.keyResults.reduce((sum, kr) => sum + kr.progress, 0);
                group.progress = Math.round((totalProgress / group.keyResults.length) * 10) / 10;
              }
            }
          });

          okrListForMember.value = Array.from(groupedMap.values());
        } else {
          okrListForMember.value = [];
        }
      } else {
        okrListForMember.value = [];
      }
    } catch (error) {
      console.error('OKR 목록 조회 실패:', error);
      okrListForMember.value = [];
    } finally {
      isOkrLoading.value = false;

      // KR 상세 보강 (performMeasMtd, performTarget)
      const allKrs = okrListForMember.value.flatMap(obj => obj.keyResults);
      enrichKrDetails(allKrs);
    }
  };

  /**
   * KR 리스트의 performMeasMtd/performTarget 보강
   * - 리스트 API에 해당 필드가 없어 KR 상세(selectKr.do)에서 가져옴
   * - performMeasMtd/performTarget은 캐시 (설정값이라 잘 안 바뀜)
   * - actual(피드백)은 매번 최신 조회 (데이터 정합성 유지)
   * @param krList - 보강할 KR 배열 (krId 필수)
   */
  const enrichKrDetails = async (krList: any[]) => {
    if (krList.length === 0) return;

    const userId = selectedMember.value || loginUserInfo.value?.userId || '';

    await Promise.all(
      krList.map(async kr => {
        const krId = kr.krId || kr.planId;
        if (!krId) return;
        try {
          // performMeasMtd/performTarget/performUnit: 캐시 우선, 없으면 API 호출
          const cached = krEnrichCache.get(krId);
          if (cached) {
            kr.performMeasMtd = cached.performMeasMtd;
            kr.performTarget = cached.performTarget;
            kr.performUnit = cached.performUnit;
            kr.weight = cached.weight;
          } else {
            const response = await kpiApi.fetchKrDetail(selectedYear.value, krId);
            const detail = response?.detailData;
            if (detail) {
              kr.performMeasMtd = detail.performMeasMtd || detail.performMeasCd || '';
              kr.performTarget = detail.performTarget || detail.target || '';
              kr.performUnit = detail.unit || '';
              kr.weight = detail.weightUseYn === 'N' ? 0 : (Number(detail.weight) || 0);
              krEnrichCache.set(krId, {
                performMeasMtd: kr.performMeasMtd,
                performTarget: kr.performTarget,
                performUnit: kr.performUnit,
                weight: kr.weight,
              });
            }
          }

          // actual(피드백)은 캐시하지 않음 — 항상 최신 조회 (모든 KR 대상, 등급 라벨 포함)
          const cycleId = kr.boardId || selectedCycleId.value;
          if (cycleId) {
            const fbResponse = await kpiApi.fetchOkrFeedback(
              selectedYear.value,
              cycleId,
              userId,
              krId,
            );
            const feedbacks = fbResponse?.feedbackDatas || [];
            // KR 목록 행 NEW 뱃지용 — 안 읽은 피드백(newYn:'Y')이 하나라도 있으면 'Y'
            kr.newYn = feedbacks.some((f: { newYn?: string }) => f?.newYn === 'Y')
              ? 'Y'
              : 'N';
            if (feedbacks.length > 0) {
              // API가 항상 시간순(desc)으로 내려주지 않아서 직접 정렬
              // - 매니저 답글이 늦게 달려도 가장 최신이 우선되도록 regDt desc → feedbackId desc
              const sortedByLatest = [...feedbacks].sort((a, b) => {
                const dateCmp = String(b?.regDt || '').localeCompare(String(a?.regDt || ''));
                if (dateCmp !== 0) return dateCmp;
                return (parseInt(b?.feedbackId) || 0) - (parseInt(a?.feedbackId) || 0);
              });

              // 실적(actual): 답글에는 보통 비어있어 actual이 채워진 최신 피드백 채택
              const latestWithActual = sortedByLatest.find(
                f => f?.actual !== undefined && f?.actual !== '' && f?.actual !== null,
              );
              if (latestWithActual) {
                kr.actual = latestWithActual.actual;
              }

              // 등급(05) 피드백: ratingId(1~5) → gradeId(s/a/b/c/d) → 대문자
              const evalMtd =
                fbResponse?.iconMap?.feedbackEvalMtd ||
                fbResponse?.feedbackEvalMng?.feedbackEvalMtd ||
                '';
              const ratingList =
                fbResponse?.feedbackEvalRatingList || fbResponse?.feedbackRatingList || [];
              if (evalMtd === '05') {
                // 매니저 평가(KR 소유자가 아닌 사람의 평가) 우선 — 날짜 무관
                // 1) 다른 사람의 ratingId 보유 피드백 중 최신
                // 2) 없으면 본인의 ratingId 보유 피드백 중 최신
                const ownerId = String(userId || '');
                const managerLatest = sortedByLatest.find(
                  f => f?.ratingId && String(f?.createUserId || '') !== ownerId,
                );
                const fallback = sortedByLatest.find(f => f?.ratingId);
                const pick = managerLatest || fallback;
                const ratingMatch = ratingList.find(
                  (r: { ratingId?: string; gradeId?: string }) =>
                    String(r.ratingId) === String(pick?.ratingId || ''),
                );
                kr.gradeLabel = (ratingMatch?.gradeId || '').toUpperCase();
              } else {
                kr.gradeLabel = '';
              }
            }
          }
        } catch {
          // 개별 실패 무시
        }
      }),
    );
  };

  /**
   * KR 상세 보강 캐시 초기화
   */
  const clearEnrichKrCache = () => {
    krEnrichCache.clear();
  };

  // ============================================
  // OKR 선택 및 토글
  // ============================================

  /**
   * 피드백 폼 초기화
   */
  const resetOkrFeedbackForm = () => {
    okrFeedbackForm.value = {
      content: '',
      achive: '',
      actual: '',
      statusCd: '',
      feedbackRatingId: '3',
      feedbackId: '',
    };
  };

  /**
   * OKR(KR) 선택
   */
  const selectOkr = async (okr: OkrRecord) => {
    selectedOkr.value = okr;
    selectedKpi.value = null;
    resetDetailData();
    resetOkrFeedbackForm();
    activeOkrDetailTab.value = '실적';
    // KR의 boardId/cycleId를 피드백 조회에 전달
    const krCycleId = okr.boardId || okr.cycleId || '';
    await Promise.all([fetchKrTaskRecords(okr.krId), fetchOkrFeedback(okr.krId, krCycleId)]);
  };

  /**
   * Objective 펼침/접힘 토글
   */
  const toggleObjective = (objId: string) => {
    const obj = okrListForMember.value.find(o => o.objId === objId);
    if (obj) {
      obj.isExpanded = !obj.isExpanded;
    }
  };

  /**
   * KPI 패널 토글
   */
  const toggleKpiPanel = () => {
    isKpiPanelExpanded.value = !isKpiPanelExpanded.value;
  };

  /**
   * OKR 패널 토글
   */
  const toggleOkrPanel = () => {
    isOkrPanelExpanded.value = !isOkrPanelExpanded.value;
  };

  // ============================================
  // KR 연결 업무 조회
  // ============================================

  /**
   * KR에 연결된 업무 목록 조회
   */
  const fetchKrTaskRecords = async (krId: string) => {
    if (!krId) {
      krTaskRecords.value = [];
      return;
    }

    isKrTaskLoading.value = true;
    try {
      const response = await kpiApi.fetchKrPlanList(
        selectedYear.value,
        selectedCycleId.value,
        selectedMember.value || loginUserInfo.value?.userId || '',
        krId,
      );

      if (response?.planList && Array.isArray(response.planList)) {
        krTaskRecords.value = response.planList.map((item: PlanResponseItem) => ({
          planId: item.planId || '',
          planNm: item.planNm || '',
          planDesc: item.content || '',
          assignee: item.assigneeNm || item.userNm || '',
          dueDate: item.endDt || '',
          progress: Number(item.achive) || 0,
          status: mapTaskStatus(item.statusCd || ''),
        }));
      } else {
        krTaskRecords.value = [];
      }
    } catch (error) {
      console.error('KR 연결 업무 조회 실패:', error);
      krTaskRecords.value = [];
    } finally {
      isKrTaskLoading.value = false;
    }
  };

  // ============================================
  // OKR 피드백
  // ============================================

  /**
   * OKR 피드백 조회
   * @param krId - KR ID
   * @param cycleId - 분기 ID (옵션, 없으면 selectedCycleId 사용)
   */
  const fetchOkrFeedback = async (krId: string, cycleId?: string) => {
    const targetUserId = selectedMember.value || loginUserInfo.value?.userId || '';
    const targetCycleId = cycleId || selectedCycleId.value;
    if (!krId || !targetUserId || !targetCycleId) {
      okrFeedbackList.value = [];
      return;
    }

    isOkrFeedbackLoading.value = true;
    try {
      const response = await kpiApi.fetchOkrFeedback(
        selectedYear.value,
        targetCycleId,
        targetUserId,
        krId,
      );

      // 피드백 아이콘 설정 (iconMap에서 feedbackEvalMtd 캡처)
      if (response?.iconMap?.feedbackEvalMtd) {
        feedbackEvalMtd.value = response.iconMap.feedbackEvalMtd;
      }

      // 상태 옵션 설정
      if (response?.statusList && Array.isArray(response.statusList)) {
        okrStatusOptions.value = response.statusList.map((item: FeedbackStatusResponseItem) => ({
          value: item.statusCd || item.cdId,
          label: item.statusNm || item.cdNm,
          color: item.color || '#999',
        }));
      }

      // 별점 옵션 설정
      if (response?.feedbackRatingList && Array.isArray(response.feedbackRatingList)) {
        feedbackRatingList.value = response.feedbackRatingList.map(
          (item: FeedbackRatingOption) => ({
            ratingId: item.ratingId || '',
            ratingScore: item.ratingScore || 0,
            gradeId: item.gradeId || '',
          }),
        );
      }

      // 피드백 목록 설정
      if (response?.feedbackDatas && Array.isArray(response.feedbackDatas)) {
        okrFeedbackList.value = response.feedbackDatas.map((item: FeedbackResponseItem) => ({
          feedbackId: item.feedbackId || '',
          upFeedbackId: item.upFeedbackId || '',
          feedbackGbn: item.feedbackGbn || 'F',
          userId: item.createUserId || item.userId || '',
          userNm: item.createUserNm || item.userNm || '',
          content: item.content || '',
          achive: String(item.achive || ''),
          statusCd: item.statusCd || '',
          statusNm: item.statusNm || '',
          statusColor: item.color || getStatusColor(item.statusCd || ''),
          ratingId: item.ratingId || '3',
          createDt: item.regDt || item.createDt || '',
          isOwner: item.editYn === 'Y',
          childCnt: item.childCnt || 0,
        }));
      } else {
        okrFeedbackList.value = [];
      }
    } catch (error) {
      console.error('OKR 피드백 조회 실패:', error);
      okrFeedbackList.value = [];
    } finally {
      isOkrFeedbackLoading.value = false;
    }
  };

  /**
   * OKR 피드백 저장
   */
  const saveOkrFeedback = async (options?: {
    upFeedbackId?: string;
    feedbackGbn?: 'F' | 'R';
    content?: string;
    ratingId?: string;
    achive?: string;
    statusCd?: string;
    feedbackId?: string;
  }) => {
    const toast = useUiToast();

    if (!selectedOkr.value || !selectedCycleId.value) {
      toast.error(kpiT('toast.okrNotSelected'));
      return false;
    }

    const content = options?.content ?? okrFeedbackForm.value.content;
    if (!content?.trim()) {
      toast.error(kpiT('toast.opinionRequired'));
      return false;
    }

    try {
      const saveData = {
        year: selectedYear.value,
        cycleId: selectedCycleId.value,
        userId: selectedMember.value,
        krId: selectedOkr.value.krId,
        content: content,
        achive: options?.achive ?? okrFeedbackForm.value.achive ?? '',
        statusCd: options?.statusCd ?? okrFeedbackForm.value.statusCd ?? '',
        feedbackRatingId: options?.ratingId ?? okrFeedbackForm.value.feedbackRatingId ?? '3',
        feedbackId: options?.feedbackId ?? okrFeedbackForm.value.feedbackId ?? '',
        upFeedbackId: options?.upFeedbackId ?? '',
        feedbackGbn: options?.feedbackGbn ?? 'F',
      };

      const response = await kpiApi.saveOkrFeedback(saveData);

      if (response?.result === 'FAIL') {
        toast.error(response.msg || kpiT('toast.feedbackSaveFailed'));
        return false;
      }

      toast.success(kpiT('toast.feedbackSaved'));
      if (!options?.upFeedbackId) {
        resetOkrFeedbackForm();
      }
      await fetchOkrFeedback(selectedOkr.value.krId);

      // KR 상태/달성률/실적값 동기화 (모든 캐시에 즉시 반영)
      const achiveValue = options?.achive ?? okrFeedbackForm.value.achive;
      const actualValue = options?.actual ?? okrFeedbackForm.value.actual;
      const statusCdValue = options?.statusCd ?? okrFeedbackForm.value.statusCd;
      const statusOption = okrStatusOptions.value.find(opt => opt.value === statusCdValue);

      syncKrStatusInAllCaches(selectedOkr.value.krId, {
        achive: achiveValue ? Number(achiveValue) : undefined,
        actual: actualValue !== undefined && actualValue !== '' ? actualValue : undefined,
        statusCd: statusCdValue || undefined,
        statusColor: statusOption?.color || undefined,
        progress: achiveValue ? Number(achiveValue) : undefined,
      });

      // 목표-업무 연계도 캐시 초기화 (상태 색상 변경 반영)
      clearGoalOkrCache();

      return true;
    } catch (error) {
      console.error('OKR 피드백 저장 실패:', error);
      toast.error(kpiT('toast.feedbackSaveFailed'));
      return false;
    }
  };

  /**
   * OKR 피드백 삭제
   */
  const deleteOkrFeedback = async (feedbackId: string) => {
    const toast = useUiToast();

    if (!selectedOkr.value || !selectedCycleId.value) {
      toast.error(kpiT('toast.okrNotSelected'));
      return false;
    }

    try {
      const response = await kpiApi.deleteOkrFeedback(
        selectedYear.value,
        selectedCycleId.value,
        selectedMember.value,
        selectedOkr.value.krId,
        feedbackId,
      );

      if (response?.result === 'FAIL') {
        toast.error(response.msg || kpiT('toast.feedbackDeleteFailed'));
        return false;
      }

      toast.success(kpiT('toast.feedbackDeleted'));
      await fetchOkrFeedback(selectedOkr.value.krId);

      // 삭제 후 최신 피드백의 actual/achive로 동기화
      const latestFeedback = okrFeedbackList.value.find(f => f.feedbackGbn !== 'R');
      syncKrStatusInAllCaches(selectedOkr.value.krId, {
        achive: latestFeedback?.achive ? Number(latestFeedback.achive) : 0,
        actual:
          latestFeedback?.actual !== undefined && latestFeedback?.actual !== ''
            ? latestFeedback.actual
            : '',
        statusCd: latestFeedback?.statusCd || '',
        statusColor: latestFeedback?.statusColor || '',
        progress: latestFeedback?.achive ? Number(latestFeedback.achive) : 0,
      });

      // 목표-업무 연계도 캐시 초기화 (상태 색상 변경 반영)
      clearGoalOkrCache();
      return true;
    } catch (error) {
      console.error('OKR 피드백 삭제 실패:', error);
      toast.error(kpiT('toast.feedbackDeleteFailed'));
      return false;
    }
  };

  /**
   * 피드백 수정 모드 설정
   */
  const editOkrFeedback = (feedback: OkrFeedbackItem) => {
    okrFeedbackForm.value = {
      content: feedback.content,
      achive: feedback.achive,
      statusCd: feedback.statusCd,
      feedbackRatingId: feedback.ratingId,
      feedbackId: feedback.feedbackId,
    };
  };

  // ============================================
  // 목표-업무 연계도 아코디언 (PerformanceGoalTable용)
  // ============================================

  /**
   * KPI 아코디언 토글
   */
  const toggleGoalKpiAccordion = async (kpiId: string) => {
    if (expandedKpiIds.has(kpiId)) {
      expandedKpiIds.delete(kpiId);
    } else {
      // 하위 OKR/Task fetch (캐시 미존재 시)
      const okrCacheKey = getOkrCacheKey(kpiId);
      const promises = [];
      if (!kpiOkrMap[okrCacheKey]) {
        promises.push(fetchGoalKpiOkrList(kpiId));
      }
      if (!kpiTaskMap[kpiId]) {
        promises.push(fetchGoalKpiTaskList(kpiId));
      }
      await Promise.all(promises);

      // 하위 데이터 유무와 관계없이 펼침 상태로 전환 (아이콘 회전으로 클릭 피드백 제공)
      // 빈 KPI일 경우 펼침 영역 내 인라인 안내문이 노출됨 (PerformanceGoalTable.vue)
      expandedKpiIds.add(kpiId);
    }
  };

  /**
   * KPI에 연결된 OKR 목록 조회
   */
  const fetchGoalKpiOkrList = async (kpiId: string) => {
    const cacheKey = getOkrCacheKey(kpiId);
    // [열람 권한 차단] OKR 열람 권한 없는 대상자면 KPI 연계 OKR도 조회하지 않음
    // (KPI 행을 펼쳐도 연계 OKR이 노출되지 않도록 — 잔여 누수 벡터 차단)
    if (!selectedMemberDomains.value.okr) {
      kpiOkrMap[cacheKey] = [];
      return;
    }
    okrLoadingCount.value++;
    try {
      // ★ userId가 빈 문자열이면 백엔드가 본인 기준 결과를 못 내려줄 수 있으므로
      //    selectedMember(검색 확정 구성원)가 없으면 로그인 사용자 ID를 대신 사용
      const response = await kpiApi.fetchOkrList(
        selectedYear.value,
        selectedMember.value || loginUserInfo.value?.userId || '',
        kpiId,
        selectedCycleId.value || undefined, // 빈 문자열이면 undefined
      );

      // categoryList에서 최신 이름 맵 생성 (boardId 있는 레코드 우선)
      const categoryNameMap = new Map<string, string>();
      (response?.categoryList || []).forEach(
        (cat: { categoryId: string; categoryNm: string; boardId?: string }) => {
          const catId = cat.categoryId;
          // boardId가 있는 레코드를 우선 사용 (분기별 수정된 이름)
          if (!categoryNameMap.has(catId) || cat.boardId) {
            categoryNameMap.set(catId, cat.categoryNm);
          }
        },
      );

      // checked === 'checked' 필터링 후, boardId 필터링 추가 (백엔드가 cycleId 무시하므로)
      let linkedKrs = (response?.planList || []).filter(
        (kr: PlanResponseItem) => kr.checked === 'checked',
      );

      // 선택된 분기(cycleId)에 해당하는 OKR만 필터링
      if (selectedCycleId.value) {
        linkedKrs = linkedKrs.filter(kr => kr.boardId === selectedCycleId.value);
      }

      // Objective로 그룹핑
      const objectiveMap: Record<string, KpiOkrMapObjective> = {};
      linkedKrs.forEach((kr: PlanResponseItem) => {
        const objId = kr.categoryId || 'unknown';
        if (!objectiveMap[objId]) {
          // categoryNameMap에서 최신 이름 사용, 없으면 kr.categoryNm 사용
          const latestCategoryNm = categoryNameMap.get(objId) || kr.categoryNm || '목표 없음';
          objectiveMap[objId] = {
            categoryId: objId,
            categoryNm: latestCategoryNm,
            boardId: kr.boardId || null,
            boardNm: cycleList.value.find(c => c.cycleId === kr.boardId)?.cycleNm || null,
            keyResults: [],
          };
        }
        // statusColor 매핑 (API가 color 필드로 반환할 수 있음, 없으면 statusCd로 변환)
        objectiveMap[objId].keyResults.push({
          ...kr,
          statusColor: kr.statusColor || kr.color || getStatusColor(kr.statusCd || ''),
        });
      });

      kpiOkrMap[cacheKey] = Object.values(objectiveMap);

      // KR 상세 보강 (performMeasMtd, performTarget)
      const allKrs = kpiOkrMap[cacheKey].flatMap(obj => obj.keyResults);
      enrichKrDetails(allKrs);
    } catch (error) {
      console.error('OKR 목록 조회 실패:', error);
      kpiOkrMap[cacheKey] = [];
    } finally {
      okrLoadingCount.value--;
    }
  };

  /**
   * KPI에 직접 연결된 업무 목록 조회
   */
  const fetchGoalKpiTaskList = async (kpiId: string) => {
    taskLoadingCount.value++;
    try {
      // ★ selectedMember가 없으면 로그인 사용자 ID를 대신 사용 (OKR과 동일 정책)
      const response = await kpiApi.fetchTaskList(
        selectedYear.value,
        selectedMember.value || loginUserInfo.value?.userId || '',
        kpiId,
      );

      const linkedTasks = (response?.planList || []).filter(
        (task: PlanResponseItem) => task.checked === 'checked',
      );

      kpiTaskMap[kpiId] = linkedTasks;
    } catch (error) {
      console.error('KPI 직접 연결 업무 조회 실패:', error);
      kpiTaskMap[kpiId] = [];
    } finally {
      taskLoadingCount.value--;
    }
  };

  /**
   * KR 아코디언 토글
   */
  const toggleGoalKrAccordion = async (krId: string) => {
    // 이미 펼쳐진 KR 재클릭 → 개별 닫기 (다른 KR은 그대로 유지)
    if (expandedKrIds.has(krId)) {
      expandedKrIds.delete(krId);
    } else {
      const cacheKey = getKrPlanCacheKey(krId);
      if (!krPlanMap[cacheKey]) {
        await fetchGoalKrPlanList(krId);
      }

      // 업무가 있는 경우에만 펼침 (여러 KR 동시 펼침 허용)
      if (krPlanMap[cacheKey] && krPlanMap[cacheKey].length > 0) {
        expandedKrIds.add(krId);
      }
    }
  };

  /**
   * KR에 연결된 Plan 목록 조회
   */
  const fetchGoalKrPlanList = async (krId: string) => {
    const cacheKey = getKrPlanCacheKey(krId);
    planLoading.value = true;
    try {
      const response = await kpiApi.fetchOkrTaskMapping(
        selectedYear.value,
        selectedMember.value || '',
        krId,
      );

      krPlanMap[cacheKey] = response?.krPlanList || [];
    } catch (error) {
      console.error('KR Plan 목록 조회 실패:', error);
      krPlanMap[cacheKey] = [];
    } finally {
      planLoading.value = false;
    }
  };

  /**
   * 미연계 OKR 목록 조회 (KPI에 연결되지 않은 OKR)
   */
  const fetchUnlinkedOkrList = async () => {
    if (!selectedYear.value) {
      unlinkedOkrList.value = [];
      return;
    }

    unlinkedOkrLoading.value = true;
    try {
      // 전체 OKR 목록 조회 (kpiId 없이)
      // ★ selectedMember가 없으면 로그인 사용자 ID를 대신 사용
      //    (구성원=전체 상태에서도 본인의 OKR이 조회되도록)
      const response = await kpiApi.fetchLinkableOkrList(
        selectedYear.value,
        selectedMember.value || loginUserInfo.value?.userId || '',
        selectedCycleId.value || undefined,
      );

      // 본인 소유 Objective ID 목록 (categoryList에 있는 것만)
      const ownCategoryIds = new Set(
        (response?.categoryList || []).map((cat: any) => cat.categoryId),
      );

      // OBJ 하위 전체 KR 표시 (KPI 매핑 여부와 무관하게 AS-IS 동일)
      let unlinkedKrs = (response?.planList || []).filter(
        (kr: PlanResponseItem) =>
          kr.boardId && ownCategoryIds.has(kr.categoryId),
      );
      // 분기 필터링: 특정 분기 선택 시 해당 분기만, "전체" 시 cycleList 내 분기만 포함
      if (selectedCycleId.value) {
        unlinkedKrs = unlinkedKrs.filter(kr => kr.boardId === selectedCycleId.value);
      } else if (cycleList.value.length > 0) {
        const validCycleIds = new Set(cycleList.value.map(c => c.cycleId));
        unlinkedKrs = unlinkedKrs.filter(kr => validCycleIds.has(kr.boardId || ''));
      }

      // categoryList 순서(서버 sortOrder) 기준으로 objectiveMap 구성
      const objectiveMap: Record<string, KpiOkrMapObjective> = {};
      (response?.categoryList || []).forEach((cat: any) => {
        const catBoardId = cat.boardId || '';
        if (!catBoardId) return;
        if (selectedCycleId.value && catBoardId !== selectedCycleId.value) return;
        if (!selectedCycleId.value && cycleList.value.length > 0) {
          const validCycleIds = new Set(cycleList.value.map(c => c.cycleId));
          if (!validCycleIds.has(catBoardId)) return;
        }
        objectiveMap[cat.categoryId] = {
          categoryId: cat.categoryId,
          categoryNm: cat.categoryNm || '목표 없음',
          boardId: catBoardId || null,
          boardNm: cycleList.value.find(c => c.cycleId === catBoardId)?.cycleNm || null,
          keyResults: [],
        };
      });

      // KR을 해당 OBJ에 채워넣기
      unlinkedKrs.forEach((kr: PlanResponseItem) => {
        const objId = kr.categoryId || 'unknown';
        if (!objectiveMap[objId]) {
          objectiveMap[objId] = {
            categoryId: objId,
            categoryNm: kr.categoryNm || '목표 없음',
            boardId: kr.boardId || null,
            boardNm: cycleList.value.find(c => c.cycleId === kr.boardId)?.cycleNm || null,
            keyResults: [],
          };
        }
        objectiveMap[objId].keyResults.push({
          ...kr,
          statusColor: kr.statusColor || kr.color || getStatusColor(kr.statusCd || ''),
        });
      });

      unlinkedOkrList.value = Object.values(objectiveMap);

      // KR 상세 보강 (performMeasMtd, performTarget)
      const allKrs = unlinkedOkrList.value.flatMap(obj => obj.keyResults);
      enrichKrDetails(allKrs);
    } catch (error) {
      console.error('미연계 OKR 조회 실패:', error);
      unlinkedOkrList.value = [];
    } finally {
      unlinkedOkrLoading.value = false;
    }
  };

  /**
   * 미연계 OKR 섹션 토글
   */
  const toggleUnlinkedSection = () => {
    isUnlinkedSectionExpanded.value = !isUnlinkedSectionExpanded.value;
    // 펼칠 때 데이터가 없으면 조회
    if (isUnlinkedSectionExpanded.value && unlinkedOkrList.value.length === 0) {
      fetchUnlinkedOkrList();
    }
  };

  /**
   * 특정 KPI의 OKR/업무 캐시 초기화 및 재조회
   */
  const refreshGoalKpiCache = async (kpiId: string) => {
    // 해당 KPI의 캐시만 삭제 (분기별 캐시 키 사용)
    const okrCacheKey = getOkrCacheKey(kpiId);
    delete kpiOkrMap[okrCacheKey];
    delete kpiTaskMap[kpiId];

    // 재조회
    await Promise.all([fetchGoalKpiOkrList(kpiId), fetchGoalKpiTaskList(kpiId)]);
  };

  /**
   * 전체 OKR 캐시 갱신 (KPI-OKR 연결 변경 시 사용)
   * - kpiOkrMap, unlinkedOkrList, okrListForMember 모두 갱신
   */
  const syncAllOkrCaches = async () => {
    const openKpiIdList = isAllExpanded.value
      ? kpiList.value.map(kpi => kpi.kpiId)
      : [...expandedKpiIds];
    const openKrIds = new Set(expandedKrIds);

    // 1. 목표테이블 캐시 초기화
    clearGoalOkrCache();

    // 열려있던 KPI 아코디언 재조회 (토글이 아닌 강제 열기)
    for (const kpiId of openKpiIdList) {
      await toggleGoalKpiAccordion(kpiId);
    }

    // 3. 미연계 OKR 재조회
    await fetchUnlinkedOkrList();

    // 전체 펼침은 개별 expanded ID를 채우지 않으므로 갱신된 목록에서 KR을 수집한다.
    if (isAllExpanded.value) {
      const objectives = [
        ...openKpiIdList.flatMap(id => kpiOkrMap[getOkrCacheKey(id)] || []),
        ...unlinkedOkrList.value,
      ];
      objectives.forEach(obj => {
        (obj.keyResults || []).forEach(kr => {
          const krId = kr.planId || kr.krId;
          if (krId) openKrIds.add(krId);
        });
      });
    }
    await Promise.all([...openKrIds].map(async krId => {
      await fetchGoalKrPlanList(krId);
      expandedKrIds.add(krId);
    }));

    // 4. 센터패널 OKR 목록 재조회
    await fetchOkrListForMember();
  };

  /**
   * OKR 저장 (Objective + KR)
   */
  const saveOkr = async (formData: {
    objId?: string;
    isNewObjective?: boolean;
    year?: string;
    cycleId?: string;
    objNm?: string;
    krNm: string;
    startDt?: string;
    endDt?: string;
    content?: string;
    performMeasMtd?: string;
    targetVal?: string;
    evalTargetGbnCd?: string;
  }) => {
    const toast = useUiToast();

    try {
      let objId = formData.objId; // Objective ID

      // 1. 새 Objective면 먼저 저장
      if (formData.isNewObjective || !objId) {
        // 새 Objective면 먼저 저장
        const objRes = await kpiApi.saveObjective({
          year: formData.year || selectedYear.value, // 연도
          cycleId: formData.cycleId || selectedCycleId.value, // 분기 ID
          objNm: formData.objNm || '', // Objective 명
        });
        objId = objRes?.objId; // Objective ID
      }

      // 2. KR 저장
      const krRes = await kpiApi.insertOkr({
        // OKR 저장
        year: formData.year || selectedYear.value, // 연도
        cycleId: formData.cycleId || selectedCycleId.value, // 분기 ID
        objId, // Objective ID
        krNm: formData.krNm, // KR 명
        startDt: formData.startDt?.replace(/[-./]/g, ''), // 시작일
        endDt: formData.endDt?.replace(/[-./]/g, ''), // 종료일
        content: formData.content, // 내용
        performMeasMtd: formData.performMeasMtd || '001', // 측정 방법
        performTarget: formData.targetVal, // 연목표
        evalTargetGbnCd: formData.evalTargetGbnCd || '001', // 평가 대상 구분 코드
      });

      toast.success(kpiT('toast.okrSaved'));

      // 목록 새로고침
      await fetchOkrListForMember(); // OKR 목록 조회

      return krRes; // OKR 저장 결과
    } catch (error) {
      console.error('OKR 저장 실패:', error);
      toast.error(kpiT('toast.okrSaveFailed'));
      throw error; // 오류 던지기
    }
  };

  // ============================================
  // OKR 복사/이동 (#2193)
  // ============================================

  /**
   * 목표테이블 스크롤 위치 + 펼침 상태를 보존하며 갱신 실행
   * - 복사/이동 후 syncAllOkrCaches가 목록을 재구성하면서
   *   (1) goal-wrapper 스크롤이 맨 위로 튀고
   *   (2) clearGoalOkrCache가 모든 아코디언을 접어(expandedKrIds 비움) 업무가 사라짐
   * - 갱신 전 스크롤/펼친 KR을 저장했다가 갱신 후 복원(업무 재조회 포함)
   */
  const runPreservingScroll = async (fn) => {
    const wrapper = typeof document !== 'undefined' ? document.querySelector('.goal-wrapper') : null;
    const top = wrapper ? wrapper.scrollTop : 0;
    const prevKrIds = [...expandedKrIds]; // 펼쳐져 있던 KR 목록(업무 표시 중)
    const wasAllExpanded = isAllExpanded.value; // 전체 펼침 모드 여부
    await fn();

    if (wasAllExpanded) {
      // 전체 펼침 모드: 갱신으로 krPlanMap이 비면 KR은 보여도 업무가 빈칸으로 남음
      // (접기/펼치기 다시 눌러야 뜨는 증상) → 화면의 모든 KR 업무를 재조회
      const krIds = new Set<string>();
      Object.values(kpiOkrMap).forEach((objs: any) =>
        (objs || []).forEach((obj: any) =>
          (obj.keyResults || []).forEach((kr: any) => krIds.add(kr.planId || kr.krId)),
        ),
      );
      unlinkedOkrList.value.forEach((obj: any) =>
        (obj.keyResults || []).forEach((kr: any) => krIds.add(kr.planId || kr.krId)),
      );
      await Promise.all([...krIds].filter(Boolean).map(id => fetchGoalKrPlanList(id)));
    } else if (prevKrIds.length) {
      // 단일 펼침 모드: 펼쳐져 있던 KR들의 업무 복원 (여러 KR 동시 펼침 지원)
      await Promise.all(
        prevKrIds.map(async krId => {
          expandedKrIds.add(krId);
          await fetchGoalKrPlanList(krId);
        }),
      );
    }

    await nextTick();
    if (wrapper) wrapper.scrollTop = top;
  };

  /**
   * KR 복사/이동 대상 목적 목록 조회 (팝오버 목적 select 옵션)
   * @param targetCycleId - 사용자가 선택한 대상 분기
   */
  const fetchKrTargetObjList = async (targetCycleId: string) => {
    const userId = selectedMember.value || loginUserInfo.value?.userId || '';
    try {
      const res = await kpiApi.fetchCopyTargetObjList(selectedYear.value, targetCycleId, userId);
      return (res?.objList || []) as { objId: string; objNm: string }[];
    } catch (error) {
      console.error('대상 목적 목록 조회 실패:', error);
      return [];
    }
  };

  /**
   * KR 복사 실행 (KR 행만 복사, 활동/피드백 제외)
   * ⚠️ sourceCycleId는 KR 자신의 분기(kr.boardId) — "전체" 모드 대응
   */
  const handleCopyKr = async (payload: {
    krId: string;
    sourceCycleId: string;
    targetCycleId: string;
    targetObjId: string;
  }) => {
    const toast = useUiToast();
    if (!payload.targetObjId) {
      toast.error(kpiT('toast.objectiveSelectForKr'));
      throw new Error('targetObjId required');
    }
    try {
      await kpiApi.copyKr(
        selectedYear.value,
        payload.sourceCycleId,
        payload.targetCycleId,
        payload.targetObjId,
        payload.krId,
      );
      toast.success(kpiT('toast.krCopySuccess'));
      await runPreservingScroll(syncAllOkrCaches);
    } catch (error) {
      console.error('KR 복사 실패:', error);
      // useApi 자동 에러 토스트와 중복되지 않도록 store toast 생략
      throw error;
    }
  };

  /**
   * KR 이동 실행 (KR의 목적/분기 재할당, 피드백·연계 함께 이동)
   * ⚠️ sourceCycleId는 KR 자신의 분기(kr.boardId) — "전체" 모드 대응
   */
  const handleMoveKr = async (payload: {
    krId: string;
    sourceCycleId: string;
    targetCycleId: string;
    targetObjId: string;
  }) => {
    const toast = useUiToast();
    if (!payload.targetObjId) {
      toast.error(kpiT('toast.objectiveSelectForKr'));
      throw new Error('targetObjId required');
    }
    try {
      await kpiApi.moveKr(
        selectedYear.value,
        payload.sourceCycleId,
        payload.targetCycleId,
        payload.targetObjId,
        payload.krId,
      );
      toast.success(kpiT('toast.krMoveSuccess'));
      await runPreservingScroll(syncAllOkrCaches);
    } catch (error) {
      console.error('KR 이동 실패:', error);
      // useApi 자동 에러 토스트와 중복되지 않도록 store toast 생략
      throw error;
    }
  };

  return {
    // 상태
    ...useOkrState(),

    // 분기 관리
    fetchCycleList,
    changeCycle,

    // OKR 목록
    fetchOkrListForMember,

    // OKR 선택/토글
    selectOkr,
    toggleObjective,
    toggleKpiPanel,
    toggleOkrPanel,

    // KR 업무
    fetchKrTaskRecords,

    // 피드백
    fetchOkrFeedback,
    saveOkrFeedback,
    deleteOkrFeedback,
    editOkrFeedback,
    resetOkrFeedbackForm,

    // 목표-업무 연계도
    getOkrCacheKey,
    getKrPlanCacheKey,
    toggleGoalKpiAccordion,
    fetchGoalKpiOkrList,
    fetchGoalKpiTaskList,
    toggleGoalKrAccordion,
    fetchGoalKrPlanList,
    clearGoalOkrCache,
    refreshGoalKpiCache,

    // [열람 권한 차단용] OKR 화면 상태 일괄 초기화
    resetOkrForMember,

    // 미연계 OKR
    fetchUnlinkedOkrList,
    toggleUnlinkedSection,

    // OKR 저장
    saveOkr,

    // KR 복사/이동 (#2193)
    fetchKrTargetObjList, // KR 복사/이동 대상 목적 목록 조회
    handleCopyKr, // KR 복사 실행
    handleMoveKr, // KR 이동 실행

    // OKR 동기화 헬퍼
    syncObjNameInAllCaches,
    syncKrNameInAllCaches,
    syncKrStatusInAllCaches,
    syncKrNewYnInAllCaches,
    syncAllOkrCaches,

    // KR 상세 보강 캐시
    clearEnrichKrCache,
  };
};
